import { useState } from "react";
import { Link as RouterLink, useParams } from "react-router";
import { Alert, Box, Button, CircularProgress, Collapse, Drawer, IconButton, ToggleButton, ToggleButtonGroup, useMediaQuery } from "@mui/material";
import { ChevronDown, Info, Plus, X } from "lucide-react";
import dayjs from "dayjs";
import PageMeta from "../../components/common/PageMeta";
import PageContainer from "../../components/common/PageContainer";
import DashboardPaper from "../../components/common/DashboardPaper";
import Badge from "../../components/ui/badge/Badge";
import { GROUPS, type ItemState, type NewOnboardingStep, type OnboardingAnswers, type OnboardingDetailItem } from "../../types/onboarding";
import { GROUP_LABELS, PROCESSOR_LABELS, STAGES, partyLabel } from "./labels";
import { findStackBrandIndexes, listNotSure, toStackSummary } from "./stack";
import { isSlackChannelLink, toSlackChannelLabel } from "../../utils/slackChannel";
import { useOnboarding } from "./useOnboarding";
import { useCompanySwitch, useSelectCompany } from "../../hooks/useCompanyId";
import { useOpenCompanyOnboarding } from "./useOpenCompanyOnboarding";
import { isAskable, isClosed, isLive, STATUS_CLASSES, toRows, type Row } from "./checklistRows";
import { PLAIN_BUTTONS } from "./buttons";
import StepPanel from "./StepPanel";
import ChaseNow from "./ChaseNow";
import AddStepDialog from "./AddStepDialog";
import AskClientDialog from "./AskClientDialog";
import HistoryDrawer from "./HistoryDrawer";

type Filter = "all" | "todo" | "waiting" | "done";

const FILTERS: { key: Filter; label: string; match: (row: Row) => boolean }[] = [
  { key: "all", label: "All", match: () => true },
  // A step on several brands shows under every filter one of its brands matches.
  { key: "todo", label: "To do", match: (r) => r.items.some((i) => i.state === "todo") },
  { key: "waiting", label: "Waiting", match: (r) => r.items.some((i) => i.state === "waiting") },
  { key: "done", label: "Done", match: (r) => isClosed(r.status) },
];

const FILTER_COLORS: Partial<Record<Filter, string>> = {
  todo: "var(--color-brand-500)", waiting: "var(--color-warning-600)", done: "var(--color-success-600)",
};
const toPillSx = (color: string) => ({
  "&.Mui-selected, &.Mui-selected:hover, .dark &.Mui-selected, .dark &.Mui-selected:hover": { backgroundColor: color, borderColor: color, color: "#fff" },
});

const STATE_BADGE: Record<ItemState, "light" | "warning" | "success"> = { todo: "light", waiting: "warning", done: "success", not_needed: "light" };
const BRAND_CHIPS = 4;

// Past four brands the chips would wrap into a wall, so one chip sums them up.
function BrandChips({ row }: { row: Row }) {
  if (row.items.length <= BRAND_CHIPS) return row.items.map((i) => <Badge key={i.itemId} size="sm" color={STATE_BADGE[i.state]}>{i.brandName}</Badge>);
  const live = row.items.filter(isLive);
  const done = live.filter((i) => i.state === "done").length;
  return <Badge size="sm" color={row.status === "waiting" ? "warning" : row.status === "done" ? "success" : "light"}>{done} / {live.length} done</Badge>;
}

type Fact = { k: string; v: string; href?: string };

const toChannelFact = (k: string, value: string): Fact =>
  isSlackChannelLink(value) ? { k, v: toSlackChannelLabel(value), href: value } : { k, v: value };

function toFacts(answers: OnboardingAnswers): Fact[] {
  const bought = [
    ...(answers.alerts.enabled ? ["alerts"] : []),
    ...(answers.representment ? ["representment"] : []),
    ...(answers.addOns ? [answers.addOns] : []),
  ];
  const brands = answers.brands.map((b) => {
    const processors = b.processors.flatMap((p) => (p === "other" ? b.otherProcessorNames ?? [] : [PROCESSOR_LABELS[p]]));
    return processors.length ? `${b.name} (${processors.join(", ")})` : b.name;
  });
  const stackBrands = findStackBrandIndexes(answers.brands).map((i) => answers.brands[i]);
  const stacks = stackBrands.map((b) => toStackSummary(b.stack, answers.representment) || "—");
  const stackRows = stacks.every((s) => s === stacks[0])
    ? stacks.slice(0, 1).map((v) => ({ k: "Stack", v }))
    : stackBrands.map((b, i) => ({ k: `Stack · ${b.name}`, v: stacks[i] }));
  return [
    { k: "Legal entity", v: answers.legalEntity },
    ...(answers.signedOn ? [{ k: "Signed", v: dayjs(answers.signedOn).format("MMM D, YYYY") }] : []),
    { k: "Client admin", v: `${answers.admin.firstName} ${answers.admin.lastName} · ${answers.admin.email}` },
    { k: "Bought", v: bought.join(", ") || "—" },
    { k: "Brands", v: brands.join(", ") },
    ...stackRows,
    ...(answers.commsChannel ? [toChannelFact("Comms channel", answers.commsChannel)] : []),
    ...(answers.supportChannel ? [toChannelFact("Support channel", answers.supportChannel)] : []),
  ];
}

function toNotSure(answers: OnboardingAnswers): string[] {
  const stackBrands = findStackBrandIndexes(answers.brands).map((i) => answers.brands[i]);
  return stackBrands.flatMap((b) => listNotSure(b.stack, answers.representment).map((field) => (stackBrands.length > 1 ? `${field} for ${b.name}` : field)));
}

export default function OnboardingChecklist() {
  const params = useParams();
  const { onboarding, error, loading, change, addStep, saving } = useOnboarding(Number(params.onboardingId));
  useSelectCompany(onboarding?.companyId ?? null);
  useCompanySwitch(onboarding?.companyId ?? null, useOpenCompanyOnboarding());
  const [filter, setFilter] = useState<Filter>("all");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Below this width the list needs the whole row; the step opens in a drawer instead of a side panel.
  const sideBySide = useMediaQuery("(min-width: 1280px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [chaseError, setChaseError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  if (!onboarding) {
    return (
      <div>
        <PageMeta title="Chargemont | Onboarding" description="Client onboarding checklist" />
        <PageContainer>
          {error && <Alert severity="error">Could not load the onboarding: {error}</Alert>}
          {loading && !error && <CircularProgress size={24} />}
        </PageContainer>
      </div>
    );
  }

  const { answers, items } = onboarding;
  const readOnly = onboarding.newerOnboardingId !== null;
  const locked = saving || readOnly;
  const live = items.filter(isLive);
  const stage = STAGES[onboarding.stage];
  const groups = GROUPS
    .map((key) => ({ key, items: items.filter((i) => i.groupKey === key) }))
    .filter((g) => g.items.length > 0)
    .map((g) => ({ ...g, rows: toRows(g.items) }));
  const allRows = groups.flatMap((g) => g.rows);
  const match = FILTERS.find((f) => f.key === filter)!.match;
  const selected = allRows.find((r) => r.key === selectedKey) ?? allRows[0];
  const highlightedKey = sideBySide || drawerOpen ? selected?.key : undefined;
  const notices = [
    ...onboarding.notices.manualRefunds.map((n) => `Refunds by hand in ${n.platform} for ${n.brandName}`),
    ...(answers.switching.enabled ? [`Switching from ${answers.switching.provider}`] : []),
  ];

  const overdueCount = items.filter((i) => i.overdue).length;
  const notSure = toNotSure(answers);

  const stepPanel = selected
    ? (
      <StepPanel
        key={selected.key} row={selected} answers={answers} items={items} createdAt={onboarding.createdAt} history={onboarding.history}
        onChange={change} saving={locked} readOnly={readOnly}
      />
    )
    : <div className="px-6 py-10 text-center text-sm text-gray-500">This checklist has no steps</div>;

  const toggleGroup = (key: string) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const askableRows = allRows.filter(isAskable);
  const askClient = async (rows: Row[]) => {
    const failed = await change(rows.flatMap((r) => r.items.filter((i) => i.state === "todo")), { state: "waiting" });
    if (!failed) setAsking(false);
    return failed;
  };
  const addStepAndClose = async (step: NewOnboardingStep) => {
    const failed = await addStep(step);
    if (!failed) setAdding(false);
    return failed;
  };
  const chase = async (item: OnboardingDetailItem) => setChaseError(await change([item], { state: "waiting" }));
  const select = (key: string | null) => {
    setSelectedKey(key);
    setDrawerOpen(true);
  };
  const openItem = (item: OnboardingDetailItem) => select(allRows.find((r) => r.items.includes(item))?.key ?? null);

  return (
    <Box sx={PLAIN_BUTTONS}>
      <PageMeta title={`Chargemont | ${onboarding.companyName} onboarding`} description="Client onboarding checklist" />
      <PageContainer>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm text-gray-500">Onboarding</span>
              <div className="flex items-center gap-2.5">
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-white/90">{onboarding.companyName}</h1>
                <Badge size="sm" color={stage.color}>{stage.label}</Badge>
              </div>
            </div>
            <div className="flex gap-8">
              {[
                onboarding.signedDays !== null
                  ? { label: "Days since signing", value: String(onboarding.signedDays) }
                  : { label: "Days in onboarding", value: String(onboarding.onboardingDays) },
                { label: "Steps done", value: `${live.filter((i) => i.state === "done").length} / ${live.length}` },
                { label: "Overdue", value: String(overdueCount), alert: overdueCount > 0 },
              ].map((s) => (
                <div key={s.label} className="flex flex-col items-end gap-0.5">
                  <span className={`text-xl font-semibold ${s.alert ? "text-error-600" : "text-gray-800 dark:text-white/90"}`}>{s.value}</span>
                  <span className="text-xs text-gray-500">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          <DashboardPaper sx={{ px: 2.5, py: 1.75 }}>
            <div className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              {toFacts(answers).map((f) => (
                <div key={f.k} className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-gray-500">{f.k}</span>
                  <span className="break-words text-gray-800 dark:text-white/90">
                    {f.href ? <a href={f.href} target="_blank" rel="noopener" className="text-brand-500 hover:underline">{f.v}</a> : f.v}
                  </span>
                </div>
              ))}
              {notices.length > 0 && (
                <div className="col-span-full flex flex-wrap gap-2">
                  {notices.map((n) => <Badge key={n} size="sm" color="warning">{n}</Badge>)}
                </div>
              )}
              <div className="col-span-full flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-2.5 text-gray-700 dark:border-gray-800 dark:text-gray-300">
                <span>
                  {answers.salesNotes && <><span className="text-gray-500">Sales notes · </span><span className="whitespace-pre-line">{answers.salesNotes}</span></>}
                </span>
                <span className="flex gap-2">
                  {!readOnly && <Button size="small" variant="outlined" component={RouterLink} to={`/onboarding/${onboarding.onboardingId}/edit`}>Edit answers</Button>}
                  <Button size="small" variant="outlined" onClick={() => setHistoryOpen(true)}>History</Button>
                </span>
              </div>
            </div>
          </DashboardPaper>
          {notSure.length > 0 && (
            <div className="flex items-center gap-3 rounded-lg border border-warning-200 bg-warning-50 px-4 py-2.5 text-sm text-warning-800 dark:border-warning-500/30 dark:bg-warning-500/15 dark:text-orange-400">
              <Info size={16} aria-hidden="true" className="flex-none" />
              <p className="min-w-0 flex-1">
                <strong className="font-semibold">{notSure.length} stack {notSure.length === 1 ? "answer" : "answers"} to confirm with the client:</strong>{" "}
                {notSure.join(", ")}. Once they answer, edit the answers and the Confirm steps close.
              </p>
              {!readOnly && <RouterLink to={`/onboarding/${onboarding.onboardingId}/edit`} className="flex-none font-medium underline hover:no-underline">Edit answers</RouterLink>}
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-start gap-5">
          <div className="flex min-w-0 flex-[999_1_480px] flex-col gap-4">
            {readOnly && (
              <Alert severity="info" action={<Button component={RouterLink} to={`/onboarding/${onboarding.newerOnboardingId}`} color="inherit" size="small">Open the newer onboarding</Button>}>
                A newer onboarding was started for this company, so this one can no longer change.
              </Alert>
            )}
            <ChaseNow items={items} saving={locked} error={chaseError} onOpen={openItem} onChase={chase} />
            <div className="flex flex-wrap items-center gap-2">
              <ToggleButtonGroup exclusive size="small" aria-label="Show steps" value={filter} onChange={(_, value: Filter | null) => value && setFilter(value)}>
                {FILTERS.map((f) => {
                  const color = FILTER_COLORS[f.key];
                  return (
                    <ToggleButton key={f.key} value={f.key} sx={color ? toPillSx(color) : undefined}>
                      {color && <span aria-hidden="true" className="mr-2 h-2 w-2 flex-none rounded-full" style={{ backgroundColor: filter === f.key ? "#fff" : color }} />}
                      {f.label}&nbsp;<span className="text-gray-400">{allRows.filter(f.match).length}</span>
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
              <span className="ml-auto flex flex-wrap items-center gap-2">
                {askableRows.length > 0 && !readOnly && (
                  <Button variant="outlined" size="small" sx={{ height: 32, borderRadius: 2, whiteSpace: "nowrap" }} onClick={() => setAsking(true)}>
                    Ask the client&nbsp;<span className="text-brand-300">{askableRows.length}</span>
                  </Button>
                )}
              </span>
            </div>

            {asking && (
              <AskClientDialog
                companyName={onboarding.companyName} rows={askableRows} saving={saving}
                onClose={() => setAsking(false)} onAsk={askClient}
              />
            )}

            <DashboardPaper sx={{ overflow: "hidden" }}>
              {groups.map((g) => {
                const rows = g.rows.filter(match);
                if (!rows.length && filter !== "all") return null;
                const groupLive = g.items.filter(isLive);
                const done = groupLive.filter((i) => i.state === "done").length;
                const overdue = g.items.filter((i) => i.overdue).length;
                const open = !collapsed.has(g.key) && rows.length > 0;
                return (
                  <div key={g.key} className="border-b border-gray-200 last:border-b-0 dark:border-gray-800">
                    <button
                      type="button" aria-expanded={open} onClick={() => toggleGroup(g.key)}
                      className="grid min-h-[52px] w-full cursor-pointer grid-cols-[16px_minmax(0,1fr)_auto] items-center gap-3 px-4 text-left sm:grid-cols-[16px_minmax(0,1fr)_auto_96px]"
                    >
                      <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? "" : "-rotate-90"}`} />
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-gray-800 dark:text-white/90">
                        {GROUP_LABELS[g.key]}
                        {overdue > 0 && <Badge size="sm" color="error">{overdue} overdue</Badge>}
                      </span>
                      <span className="text-sm text-gray-500">{done} of {groupLive.length}</span>
                      <span className="hidden h-1.5 overflow-hidden rounded-full bg-gray-100 sm:block dark:bg-gray-800">
                        <span className="block h-1.5 bg-success-500" style={{ width: `${groupLive.length ? Math.round((done / groupLive.length) * 100) : 100}%` }} />
                      </span>
                    </button>
                    <Collapse in={open} unmountOnExit>
                      {rows.map((r) => (
                        <div key={r.key} className={`flex items-center border-t border-gray-100 dark:border-gray-800 ${r.key === highlightedKey ? "bg-brand-25 dark:bg-brand-500/10" : ""}`}>
                          <button
                            type="button" onClick={() => select(r.key)} aria-current={r.key === highlightedKey ? "true" : undefined}
                            className="grid min-h-12 min-w-0 flex-1 cursor-pointer grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1 py-2 pl-10 pr-4 text-left sm:grid-cols-[minmax(0,1fr)_120px_140px]"
                          >
                            <span className="col-span-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 sm:col-span-1">
                              <span className={`text-sm ${isClosed(r.status) ? "text-gray-400" : "text-gray-800 dark:text-white/90"} ${r.status === "not_needed" ? "line-through" : ""}`}>{r.title}</span>
                              {r.items[0].brandName !== null && <BrandChips row={r} />}
                            </span>
                            <span><Badge size="sm" color="light">{partyLabel(r.items[0])}</Badge></span>
                            <span className={`text-sm font-medium ${r.overdue ? "text-error-600" : STATUS_CLASSES[r.status]}`}>{r.label}</span>
                          </button>
                        </div>
                      ))}
                    </Collapse>
                  </div>
                );
              })}
            </DashboardPaper>
            {!readOnly && (
              <Button size="small" startIcon={<Plus size={16} />} sx={{ alignSelf: "flex-start" }} onClick={() => setAdding(true)}>Add step</Button>
            )}
            {adding && <AddStepDialog saving={saving} onClose={() => setAdding(false)} onAdd={addStepAndClose} />}
          </div>

          {sideBySide ? (
            <aside aria-label="Step details" className="sticky top-6 min-w-0 max-w-[400px] flex-[1_1_340px]">
              <DashboardPaper>{stepPanel}</DashboardPaper>
            </aside>
          ) : (
            <Drawer anchor="right" open={drawerOpen && !!selected} onClose={() => setDrawerOpen(false)} slotProps={{ paper: { sx: { width: "min(440px, 100vw)" } } }}>
              <Box component="aside" aria-label="Step details" sx={PLAIN_BUTTONS}>
                <div className="flex justify-end px-3 pt-3">
                  <IconButton size="small" aria-label="Close" onClick={() => setDrawerOpen(false)}><X size={18} /></IconButton>
                </div>
                {stepPanel}
              </Box>
            </Drawer>
          )}
        </div>
      </PageContainer>
      <HistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} onboarding={onboarding} />
    </Box>
  );
}
